import { useState, useRef, useContext, useEffect } from "react";
import companyBasedRequest from "../../request/companyBasedRequest";
import { CombinedContext } from "../../context/combinedContext";
import generalRequest from "../../request/generalRequest";
import "./profileAdminCard.css";
import BookingModal from "../bookingModal/bookingModal";
import translations from "../../translations/translations";
import ShopInfoModal from "./shopInfoModal/shopInfoModal";
import ImageModal from "./imageModal/imageModal";
import ShopHoursModal from "./shopHoursModal/shopHoursModal";
import { Camera, Edit, Flag, Phone, User2 } from "lucide-react";
import ShopContactModal from "./shopContactModal/shopContactModal";
import { Image as ImageIcon } from "lucide-react";
import AdminServicesModal from "./AdminServicesModal/AdminServicesModal";
import AttributesModal from "./attributesModal/attributesModal";
import ReportReviewModalAdmin from "../reportReviewModal/reportReviewModalAdmin";
import { useLocation, useNavigate } from "react-router-dom";
import ProfileAdminSkeleton from "./profileAdminSkeleton/profileAdminSkeleton";
import ShopMapPreview from "../../utils/shopMap/shopMapPreview";
import AdminServiceCategoriesModal from "./AdminServiceCategoriesModal/AdminServiceCategoriesModal";

function ProfileCarousel({ images = [], rating = "5.0", reviews = 0 }) {
    const trackRef = useRef(null);
    const [idx, setIdx] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const startX = useRef(0);
    const currentX = useRef(0);
    const deltaX = useRef(0);

    const { language } = useContext(CombinedContext);

    const hasImages = images && images.length > 0;
    const safeImages = hasImages ? images : [];

    const getImageUrl = (img) => {
        if (!img) return null;
        return img.startsWith("http")
            ? img
            : `https://${process.env.REACT_APP_IMAGES_BUCKET}.s3.${process.env.REACT_APP_APP_REGION}.amazonaws.com/${img}`;
    };

    const goTo = (n) => {
        const total = safeImages.length;
        setIdx((n + total) % total);
    };

    const next = () => goTo(idx + 1);
    const prev = () => goTo(idx - 1);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [idx]);

    const onTouchStart = (e) => {
        setIsDragging(true);
        startX.current = e.touches ? e.touches[0].clientX : e.clientX;
        currentX.current = startX.current;
        deltaX.current = 0;
    };

    const onTouchMove = (e) => {
        if (!isDragging) return;
        currentX.current = e.touches ? e.touches[0].clientX : e.clientX;
        deltaX.current = currentX.current - startX.current;
        if (trackRef.current) {
            trackRef.current.style.transition = "none";
            trackRef.current.style.transform = `translateX(calc(${-idx * 100}% + ${deltaX.current}px))`;
        }
    };
    const onTouchEnd = () => {
        setIsDragging(false);
        if (Math.abs(deltaX.current) > 60) {
            if (deltaX.current < 0) next();
            else prev();
        }
        if (trackRef.current) {
            trackRef.current.style.transition = "";
            trackRef.current.style.transform = `translateX(${-idx * 100}%)`;
        }
        deltaX.current = 0;
    };

    useEffect(() => {
        if (trackRef.current) {
            trackRef.current.style.transition = "transform 300ms ease";
            trackRef.current.style.transform = `translateX(${-idx * 100}%)`;
        }
    }, [idx]);

    return (
        <div className="sf-profile-carousel" aria-roledescription="carousel">
            <div
                className="sf-profile-carousel-viewport"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onMouseDown={(e) => {
                    e.preventDefault();
                    onTouchStart(e);
                    window.addEventListener("mousemove", onTouchMove);
                    window.addEventListener(
                        "mouseup",
                        () => {
                            onTouchEnd();
                            window.removeEventListener("mousemove", onTouchMove);
                        },
                        { once: true }
                    );
                }}
            >
                {hasImages ? (
                    <div className="sf-profile-carousel-track" ref={trackRef}>
                        {safeImages.map((img, i) => (
                            <div className="sf-profile-carousel-slide" key={i} aria-hidden={i !== idx}>
                                <img
                                    className="sf-profile-carousel-img"
                                    src={getImageUrl(img)}
                                    alt={`Gallery ${i + 1}`}
                                    draggable={false}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="sf-profile-carousel-empty">
                        <ImageIcon size={42} strokeWidth={1.5} />
                        <p>{translations[language].profile.images.noPhotos}</p>
                    </div>
                )}

                {hasImages && safeImages.length > 1 && (
                    <>
                        <button className="sf-profile-carousel-arrow sf-profile-carousel-arrow--left" onClick={prev}>
                            ‹
                        </button>
                        <button className="sf-profile-carousel-arrow sf-profile-carousel-arrow--right" onClick={next}>
                            ›
                        </button>
                    </>
                )}
            </div>

            {hasImages && safeImages.length > 1 && (
                <div className="sf-profile-carousel-dots">
                    {safeImages.map((_, i) => (
                        <button
                            key={i}
                            className={`sf-profile-carousel-dot ${i === idx ? "is-active" : ""}`}
                            onClick={() => goTo(i)}
                            aria-label={`Go to slide ${i + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

const ProfileAdminCard = () => {
    const { userData, language } = useContext(CombinedContext);
    const [shopData, setShopData] = useState(null);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAllHours, setShowAllHours] = useState(false);
    const [activeModal, setActiveModal] = useState(null);
    const [reportReviewId, setReportReviewId] = useState(null);
    const [activeCategory, setActiveCategory] = useState(null);

    const fileInputRef = useRef(null);
    const staffListRef = useRef(null);
    const isDraggingRef = useRef(false);
    const startXRef = useRef(0);
    const scrollLeftRef = useRef(0);
    const velocityRef = useRef(0);
    const lastXRef = useRef(0);
    const categoryRefs = useRef({});
    let momentumID = null;

    const location = useLocation();

    const navigate = useNavigate();

    useEffect(() => {
        if (!location.state?.openModal) return;

        setActiveModal(location.state.openModal);
    }, [location.state, navigate]);

    useEffect(() => {
        async function loadData() {
            try {
                if (!userData?.user?.shopId) return;
                const shopRes = await companyBasedRequest.getShopWithstaffsByIdPrivate(
                    userData.user.shopId,
                    userData.cognitoTokens.AccessToken
                );
                setShopData(shopRes.shop);
                setImages(shopRes.shop.images || []);
                setLoading(false);
            } catch (err) {
                console.error("Error loading data:", err);
            }
        }
        loadData();
    }, [userData]);

    useEffect(() => {
        if (shopData?.serviceCategories?.length && !activeCategory) {
            setActiveCategory(shopData.serviceCategories[0].categoryId);
        }
    }, [shopData, activeCategory]);

    const openModal = (name) => setActiveModal(name);
    const closeModal = () => setActiveModal(null);

    if (loading || !shopData) return <ProfileAdminSkeleton />;

    const handleStaffMouseDown = (e) => {
        const slider = staffListRef.current;
        if (!slider) return;

        isDraggingRef.current = true;
        slider.classList.add("is-dragging");
        startXRef.current = e.pageX - slider.offsetLeft;
        scrollLeftRef.current = slider.scrollLeft;
    };

    const handleStaffMouseLeave = () => {
        const slider = staffListRef.current;
        if (!slider) return;

        isDraggingRef.current = false;
        slider.classList.remove("is-dragging");
    };

    const handleStaffMouseUp = () => {
        const slider = staffListRef.current;
        if (!slider) return;

        isDraggingRef.current = false;
        slider.classList.remove("is-dragging");
    };

    const handleStaffMouseMove = (e) => {
        const slider = staffListRef.current;
        if (!slider || !isDraggingRef.current) return;

        e.preventDefault();
        const x = e.pageX - slider.offsetLeft;
        const walk = (x - startXRef.current) * 1; // drag speed
        slider.scrollLeft = scrollLeftRef.current - walk;
    };

    const servicesByCategory = (shopData?.serviceCategories || []).map((cat) => ({
        category: cat,
        services: (shopData.services || []).filter((s) => s.categoryId === cat.categoryId),
    }));

    function formatPhone(number) {
        if (!number) return "";
        // remove all non-digits
        const digits = number.replace(/\D/g, "");

        // US/Canada format (E.164: +1xxxxxxxxxx)
        if (digits.length === 11 && digits.startsWith("1")) {
            return "+1 " + "(" + digits.slice(1, 4) + ") " + digits.slice(4, 7) + "-" + digits.slice(7);
        }

        // fallback (just add + if missing)
        return number.startsWith("+") ? number : "+" + number;
    }

    function handleReportReview(reviewId) {
        setReportReviewId(reviewId);
        setActiveModal("reportReview");
    }

    const rating = shopData.reviews?.averageRating?.toFixed(1) || "5.0";
    const reviewCount = shopData.reviews?.total || 0;
    const addressQuery = encodeURIComponent(`${shopData.address}, ${shopData.city}, ${shopData.country}`);

    const renderStars = (rating) => {
        const stars = [];

        for (let i = 1; i <= 5; i++) {
            stars.push(
                <span key={i} className={i <= rating ? "sf-star sf-star-filled" : "sf-star sf-star-empty"}>
                    ★
                </span>
            );
        }

        return stars;
    };

    function formatTime24To12(time24, language = "en") {
        if (!time24) return "";

        const [h, m] = time24.split(":").map(Number);
        const hour12 = h % 12 || 12;
        const minutes = m.toString().padStart(2, "0");
        const period = h >= 12 ? "PM" : "AM";

        // If you ever want ES = 24h, you can branch here
        return `${hour12}:${minutes} ${period}`;
    }

    const formatUrl = (url) => {
        if (!url) return "";
        if (url.startsWith("http://") || url.startsWith("https://")) {
            return url;
        }
        return `https://${url}`;
    };

    return (
        <div className="sf-profile-container">
            <div className="sf-profile-content">
                {/* LEFT SIDE (MAIN) */}
                <div className="sf-profile-main">
                    {/* ===== IMAGES / CAROUSEL ===== */}
                    <div className="sf-profile-carousel">
                        {/* Image Carousel */}
                        <ProfileCarousel images={shopData.images || []} />

                        {/* Rating Badge (inside image area) */}
                        <div className="sf-carousel-rating">
                            <span className="sf-rating-score">{rating}</span>
                            <span className="sf-rating-reviews">
                                {reviewCount} {translations[language].profile.images.reviews}
                            </span>
                        </div>

                        <button className="sf-carousel-edit-btn" onClick={() => openModal("images")}>
                            <Camera size={16} strokeWidth={1.5} className="sf-icon" />
                        </button>
                    </div>

                    {/* ===== BASIC INFO ===== */}
                    <div className="sf-profile-section-name">
                        <h1 className="sf-profile-name">
                            {shopData.name || (
                                <span className="sf-placeholder">{translations[language].profile.addShopName}</span>
                            )}
                        </h1>

                        <button className="sf-edit-btn sf-edit-btn-centered" onClick={() => openModal("info")}>
                            <Edit size={14} />
                        </button>
                    </div>

                    <p className="sf-profile-description">
                        {shopData.description || (
                            <span className="sf-placeholder">{translations[language].profile.addDescription}</span>
                        )}
                    </p>

                    <p className="sf-profile-location">
                        {shopData.address ? (
                            `${shopData.address}`
                        ) : (
                            <span className="sf-placeholder">{translations[language].profile.addAddress}</span>
                        )}
                    </p>

                    {/* ===== SERVICES ===== */}

                    <h2 className="sf-profile-section-title">
                        {translations[language].profile.services.title}
                        <button className="sf-edit-btn" onClick={() => openModal("services")}>
                            <Edit size={14} />
                        </button>
                    </h2>

                    {shopData.services?.length > 0 ? (
                        <div className="sf-profile-service-list">
                            {/* ===== CATEGORY TABS ===== */}
                            <div className="sf-profile-tabs sf-profile-tabs--sticky">
                                {shopData.serviceCategories.map((cat) => (
                                    <button
                                        key={cat.categoryId}
                                        className={`sf-profile-tab-btn ${
                                            activeCategory === cat.categoryId ? "active" : ""
                                        }`}
                                        onClick={() => {
                                            setActiveCategory(cat.categoryId);
                                            categoryRefs.current[cat.categoryId]?.scrollIntoView({
                                                behavior: "smooth",
                                                block: "start",
                                            });
                                        }}
                                    >
                                        {cat.name?.[language]}
                                    </button>
                                ))}

                                {/* ✅ CATEGORY EDIT BUTTON */}
                                <button
                                    className="sf-category-edit"
                                    onClick={() => openModal("category")}
                                    aria-label="Edit categories"
                                >
                                    <Edit size={12} />
                                </button>
                            </div>

                            {/* ===== CATEGORY SECTIONS ===== */}
                            {servicesByCategory.map(({ category, services }) => (
                                <div
                                    key={category.categoryId}
                                    ref={(el) => (categoryRefs.current[category.categoryId] = el)}
                                    className="sf-profile-category-section"
                                >
                                    <h3 className="sf-profile-category-title">{category.name?.[language]}</h3>

                                    {services.length > 0 ? (
                                        services.map((s) => (
                                            <div key={s.serviceId}>
                                                <div className="sf-profile-service-item">
                                                    <div className="sf-profile-service-info">
                                                        <div className="sf-profile-service-name">
                                                            {s.name}
                                                            {s.isHomeService && (
                                                                <span className="sf-profile-service-home">
                                                                    <span className="sf-profile-dot"> · </span>
                                                                    {language === "es" ? "A domicilio" : "Home service"}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="sf-profile-service-duration">
                                                            {s.description}
                                                        </div>
                                                    </div>

                                                    <div className="sf-profile-service-right">
                                                        <div className="sf-profile-service-price">
                                                            {s.currency || "USD"} {s.price ?? 0}
                                                        </div>
                                                        <div className="sf-profile-service-duration">
                                                            {s.duration} min
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="sf-service-divider-shop" aria-hidden="true" />
                                            </div>
                                        ))
                                    ) : (
                                        <div className="sf-empty-category">
                                            {language === "es"
                                                ? "No hay servicios en esta categoría"
                                                : "No services in this category"}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="sf-empty-block" onClick={() => openModal("services")}>
                            {translations[language].profile.addFirstServices}
                        </div>
                    )}

                    {/* ===== SAVE BUTTON ===== */}
                    <div className="sf-profile-section">
                        <h2 className="sf-profile-section-title">
                            Attributes
                            <button className="sf-edit-btn" onClick={() => openModal("attributes")}>
                                <Edit size={14} />
                            </button>
                        </h2>

                        {shopData.attributes?.length > 0 ? (
                            <div className="sf-attr-grid">
                                {shopData.attributes.map((attr) => (
                                    <div className="sf-attr-chip" key={attr.attributeId}>
                                        {attr.icon ? (
                                            <img src={attr.icon} className="sf-attr-chip-icon" alt={attr.nameEN} />
                                        ) : (
                                            <div className="sf-attr-chip-placeholder" />
                                        )}

                                        <span className="sf-attr-chip-label">
                                            {language === "es" ? attr.nameES : attr.nameEN}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="sf-empty-block" onClick={() => openModal("attributes")}>
                                {translations[language].profile.addAttributes}
                            </div>
                        )}
                    </div>

                    <div className="sf-profile-reviews-section">
                        {shopData.reviews?.length > 0 && (
                            <div className="sf-profile-rsum-container">
                                {/* LEFT: Average Score */}
                                <div className="sf-profile-rsum-left">
                                    <div className="sf-profile-rsum-score">
                                        {(
                                            shopData.reviewSummary?.averageRating ||
                                            shopData.reviews.reduce((s, r) => s + r.rating, 0) / shopData.reviews.length
                                        ).toFixed(1)}
                                    </div>
                                    <div className="sf-profile-rsum-total">{shopData.reviews.length} reviews</div>
                                </div>

                                {/* RIGHT: Distribution */}
                                <div className="sf-profile-rsum-bars">
                                    {[5, 4, 3, 2, 1].map((star) => {
                                        const count = shopData.reviews.filter((r) => r.rating === star).length;
                                        const total = shopData.reviews.length;
                                        const percent = total > 0 ? (count / total) * 100 : 0;

                                        return (
                                            <div key={star} className="sf-profile-rsum-row">
                                                <div className="sf-profile-rsum-star">{star} ★</div>

                                                <div className="sf-profile-rsum-bar">
                                                    <div
                                                        className="sf-profile-rsum-bar-fill"
                                                        style={{
                                                            width: `${percent}%`,
                                                        }}
                                                    />
                                                </div>

                                                <div className="sf-profile-rsum-count">{count}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <h2 className="sf-profile-section-title">Reviews</h2>

                        {Array.isArray(shopData.reviews) && shopData.reviews.length > 0 ? (
                            <div className="sf-profile-reviews-list">
                                {shopData.reviews.map((rev) => (
                                    <div key={rev.reviewId} className="sf-review-item">
                                        {/* Header: Name + Rating */}
                                        <div className="sf-review-header">
                                            <div className="sf-review-header-left">
                                                <div className="sf-review-name">{rev.customerName || "Anonymous"}</div>
                                            </div>

                                            <div className="sf-review-header-right">
                                                <div className="sf-review-rating">{renderStars(rev.rating)}</div>

                                                {/* REPORT BUTTON */}
                                                <button
                                                    className="sf-review-report-btn"
                                                    onClick={() => handleReportReview(rev.reviewId)}
                                                    title="Report review"
                                                >
                                                    <Flag size={14} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Services */}
                                        {rev.services?.length > 0 && (
                                            <div className="sf-review-services">
                                                {rev.services.map((srv, i) => (
                                                    <div key={i} className="sf-review-service-chip">
                                                        {language === "es" ? srv.serviceNameEs : srv.serviceNameEn}
                                                        &nbsp;•&nbsp;
                                                        <span className="sf-review-service-duration">
                                                            {srv.duration} min
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Comment */}
                                        <div className="sf-review-comment">{rev.comment || "No comment provided."}</div>

                                        {/* Timestamp */}
                                        <div className="sf-review-date">
                                            {new Date(rev.createdAt).toLocaleDateString()}
                                        </div>

                                        <div className="sf-review-divider" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="sf-empty-block">{translations[language].profile.noReviewsyet}</div>
                        )}
                    </div>
                </div>

                {/* ===== ATTRIBUTES ===== */}

                {/* RIGHT SIDE (SIDEBAR) */}
                <div className="sf-profile-sidebar">
                    <div className="sf-profile-sidebar-section">
                        <h3 className="sf-profile-sidebar-title">
                            {translations[language].profile.sidebar.location.title}
                        </h3>
                        <ShopMapPreview lat={shopData.lat} lng={shopData.lng} />
                    </div>
                    <div className="sf-shop-sidebar-section">
                        <div className="sf-shop-sidebar-title-row">
                            <h3 className="sf-shop-sidebar-title">{translations[language].shopPage.sidebar.staff}</h3>

                            {/* Only show edit if user is admin or we're in editMode */}

                            <button className="sf-edit-btn" onClick={() => navigate("/team")}>
                                <Edit size={14} />
                            </button>
                        </div>

                        <div
                            className="sf-profile-staff-list"
                            ref={staffListRef}
                            onMouseDown={handleStaffMouseDown}
                            onMouseLeave={handleStaffMouseLeave}
                            onMouseUp={handleStaffMouseUp}
                            onMouseMove={handleStaffMouseMove}
                        >
                            {shopData.staffs?.map((b) => (
                                <div className="sf-profile-staff-item" key={b.staffId || b.email}>
                                    {/* Avatar */}
                                    {b.photo ? (
                                        <img
                                            className="sf-profile-staff-avatar"
                                            src={b.photo}
                                            alt={b.firstName || "Staff"}
                                        />
                                    ) : (
                                        <div className="sf-profile-staff-avatar-fallback">
                                            <span>{b.firstName?.charAt(0).toUpperCase()}</span>
                                        </div>
                                    )}

                                    {/* Name */}
                                    <div className="sf-profile-staff-name">
                                        {b.firstName || b.email?.split("@")[0] || "Invited"}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="sf-profile-sidebar-section">
                        <div className="sf-shop-sidebar-title-row">
                            <h3 className="sf-profile-sidebar-title">
                                {translations[language].profile.sidebar.contact.title}
                            </h3>
                            <button className="sf-edit-btn" onClick={() => openModal("contact")}>
                                <Edit size={14} />
                            </button>
                        </div>
                        <p className="sf-profile-contact">
                            <Phone size={16} strokeWidth={1.6} className="sf-profile-contact-icon" />
                            <span>
                                {shopData.phone
                                    ? formatPhone(shopData.phone)
                                    : translations[language].profile.sidebar.contact.noPhone}
                            </span>
                        </p>

                        <hr className="sf-shop-divider" />

                        {shopData.openingHours && shopData.openingHours.length > 0 ? (
                            <div>
                                <div className="sf-shop-sidebar-title-row">
                                    <h3 className="sf-profile-sidebar-title">
                                        {translations[language].profile.sidebar.hours.title}
                                    </h3>

                                    <button className="sf-edit-btn" onClick={() => openModal("hours")}>
                                        <Edit size={14} />
                                    </button>
                                </div>

                                <div className="sf-shop-hours-full">
                                    {shopData.openingHours.map((day, index) => {
                                        const isToday = new Date().getDay() === index;
                                        const dayName = language === "es" ? day.dayES : day.dayEN;

                                        return (
                                            <div
                                                key={index}
                                                className={`sf-shop-hours-row ${isToday ? "is-today" : ""}`}
                                            >
                                                <div className="sf-shop-hours-day">{dayName}</div>
                                                <div className="sf-shop-hours-time">
                                                    {day.closed
                                                        ? translations[language].profile.sidebar.hours.closed
                                                        : `${formatTime24To12(day.open, language)} - ${formatTime24To12(
                                                              day.close,
                                                              language
                                                          )}`}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="sf-shop-sidebar-title-row">
                                    <h3 className="sf-profile-sidebar-title">
                                        {translations[language].profile.sidebar.hours.title}
                                    </h3>

                                    <button className="sf-edit-btn" onClick={() => openModal("hours")}>
                                        <Edit size={14} />
                                    </button>
                                </div>
                                <span className="sf-placeholder">
                                    {language === "es" ? "Añadir horario de apertura" : "Add opening hours"}
                                </span>
                            </div>
                        )}

                        {shopData.socialLinks && Object.values(shopData.socialLinks).some((link) => link) && (
                            <div>
                                <hr className="sf-shop-divider" />
                                <h3 className="sf-profile-sidebar-title">
                                    {translations[language].shopPage.sidebar.socialTitle}
                                </h3>
                                <div className="sf-shop-socials">
                                    {shopData.socialLinks.instagram && (
                                        <div className="sf-social-item">
                                            <a
                                                href={formatUrl(shopData.socialLinks.instagram)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="sf-social-link"
                                            >
                                                <img
                                                    src="https://cdn-icons-png.flaticon.com/512/1384/1384031.png"
                                                    alt="Instagram"
                                                />
                                            </a>
                                            <p>Instagram</p>
                                        </div>
                                    )}

                                    {shopData.socialLinks.facebook && (
                                        <div className="sf-social-item">
                                            <a
                                                href={formatUrl(shopData.socialLinks.facebook)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="sf-social-link"
                                            >
                                                <img
                                                    src="https://cdn-icons-png.flaticon.com/512/1384/1384005.png"
                                                    alt="Facebook"
                                                />
                                            </a>
                                            <p>Facebook</p>
                                        </div>
                                    )}

                                    {shopData.socialLinks.tiktok && (
                                        <div className="sf-social-item">
                                            <a
                                                href={formatUrl(shopData.socialLinks.tiktok)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="sf-social-link"
                                            >
                                                <img
                                                    src="https://cdn-icons-png.flaticon.com/512/3046/3046120.png"
                                                    alt="TikTok"
                                                />
                                            </a>
                                            <p>TikTok</p>
                                        </div>
                                    )}

                                    {shopData.socialLinks.website && (
                                        <div className="sf-social-item">
                                            <a
                                                href={formatUrl(shopData.socialLinks.website)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="sf-social-link"
                                            >
                                                <img
                                                    src="https://cdn-icons-png.flaticon.com/512/545/545674.png"
                                                    alt="Website"
                                                />
                                            </a>
                                            <p>{translations[language].shopPage.sidebar.websiteTitle}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    {/* SOCIAL MEDIA */}
                </div>
            </div>

            {activeModal === "info" && (
                <ShopInfoModal open={true} onClose={closeModal} shopData={shopData} setShopData={setShopData} />
            )}
            {activeModal === "services" && (
                <AdminServicesModal
                    isOpen={true}
                    onClose={closeModal}
                    shopId={shopData.shopId}
                    categories={shopData.serviceCategories}
                    initialServices={shopData.services || []}
                    servicesToAdd={[]}
                    onSave={(updated) => {
                        setShopData((prev) => ({ ...prev, services: updated }));
                    }}
                    onOpenCategories={() => {
                        setActiveModal("category");
                    }}
                />
            )}
            {activeModal === "images" && (
                <ImageModal
                    isOpen={true}
                    shopId={shopData.shopId}
                    initialImages={shopData.images || []}
                    onClose={closeModal}
                    onSave={(updatedImages) => {
                        setShopData((prev) => ({
                            ...prev,
                            images: updatedImages,
                        }));
                    }}
                />
            )}
            {activeModal === "hours" && (
                <ShopHoursModal open={true} onClose={closeModal} shopData={shopData} setShopData={setShopData} />
            )}
            {activeModal === "contact" && (
                <ShopContactModal open={true} onClose={closeModal} shopData={shopData} setShopData={setShopData} />
            )}
            {activeModal === "attributes" && (
                <AttributesModal
                    open={true}
                    onClose={closeModal}
                    shopId={shopData.shopId}
                    shopData={shopData}
                    setShopData={setShopData}
                />
            )}
            {activeModal === "reportReview" && (
                <ReportReviewModalAdmin
                    open={true}
                    onClose={() => {
                        setReportReviewId(null);
                        closeModal();
                    }}
                    reviewId={reportReviewId}
                    shopId={shopData.shopId}
                />
            )}
            {activeModal === "category" && (
                <AdminServiceCategoriesModal
                    isOpen
                    language={language}
                    shopId={shopData.shopId}
                    initialCategories={shopData.serviceCategories}
                    onClose={() => {
                        closeModal();
                    }}
                    onSave={(updated) => {
                        // setDraftCategories(updated);
                        // setIsDirty(true);
                    }}
                />
            )}
        </div>
    );
};

export default ProfileAdminCard;
